import { useAuth } from "@/lib/auth";
import { getLastAttachError } from "@/lib/protocol-actor";
import { getRuntimeDiagnostics } from "@/lib/runtime-diagnostics";
import { useMemo, useState } from "react";

/**
 * Banner when SPA lacks protocol env or actor failed after connect.
 * Critical for assets builds and dual local origins (127.0.0.1 vs *.localhost).
 */
export function EnvDiagnosticsBanner() {
	const {
		isAuthenticated,
		actor,
		isInitializing,
		configError,
		actorError,
		reconnectProtocol,
	} = useAuth();
	const [open, setOpen] = useState(false);
	const d = useMemo(() => getRuntimeDiagnostics(), []);
	const origin =
		typeof window !== "undefined" ? window.location.origin : "";
	const pageUrl =
		typeof window !== "undefined" ? window.location.href : "";

	const missingEnv = !d.hasProtocolEnv;
	const actorMissing =
		d.hasProtocolEnv && isAuthenticated && !actor && !isInitializing;
	if (!missingEnv && !actorMissing && !configError) return null;

	const attachDetail = actorError || getLastAttachError();

	return (
		<div className="border-b border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-950 dark:text-amber-100">
			<div className="container mx-auto flex flex-wrap items-start justify-between gap-2">
				<div className="min-w-0 space-y-1">
					{missingEnv ? (
						<p className="font-semibold">
							Protocol env not baked into this SPA — vaults, balances, and
							writes will not work.
						</p>
					) : null}
					{actorMissing ? (
						<p className="font-semibold">
							Connected but protocol actor is missing
							{attachDetail ? `: ${attachDetail}` : "."}
						</p>
					) : null}
					{configError ? <p>{configError}</p> : null}
					{actorMissing || missingEnv ? (
						<p className="text-[11px] opacity-90">
							<strong>Local SPA URL (official):</strong>{" "}
							<code className="rounded bg-black/10 px-1 dark:bg-black/30">
								http://&lt;assets-id&gt;.localhost:8000/
							</code>
							. Query form{" "}
							<code className="rounded bg-black/10 px-1 dark:bg-black/30">
								127.0.0.1:8000/?canisterId=
							</code>{" "}
							redirects there (icons/JS need the subdomain). Agent API stays{" "}
							<code className="rounded bg-black/10 px-1">127.0.0.1:8000</code>.
							Logout → hard refresh → Connect.
						</p>
					) : null}
					<p className="text-[11px] opacity-90">
						{d.isViteDev
							? "Vite dev uses frontend-v2/.env.local — restart pnpm dev after env changes."
							: "Assets SPA uses env from last build. Re-run: MOTOKO_SPA=v2 bash scripts/spa-build.sh && icp deploy assets -e local -y"}
					</p>
					{open ? (
						<pre className="mt-1 max-h-40 overflow-auto rounded-lg bg-black/10 p-2 font-mono text-[10px] dark:bg-black/40">
							{JSON.stringify(
								{
									pageUrl,
									origin,
									hasProtocolEnv: d.hasProtocolEnv,
									protocolId: d.protocolId || null,
									host: d.host,
									network: d.network,
									fetchRootKey: d.fetchRootKey,
									certVerifyFailure: d.certVerifyFailure,
									ledgers: d.ledgers,
									authenticated: isAuthenticated,
									actor: Boolean(actor),
									actorError: attachDetail,
									isViteDev: d.isViteDev,
								},
								null,
								2,
							)}
						</pre>
					) : null}
				</div>
				<div className="flex shrink-0 flex-col gap-1">
					{actorMissing ? (
						<button
							type="button"
							className="rounded-lg border border-amber-600/40 px-2 py-1 font-semibold"
							onClick={() => void reconnectProtocol()}
						>
							Reconnect
						</button>
					) : null}
					<button
						type="button"
						className="rounded-lg border border-amber-600/40 px-2 py-1 font-semibold"
						onClick={() => setOpen((o) => !o)}
					>
						{open ? "Hide details" : "Details"}
					</button>
				</div>
			</div>
		</div>
	);
}
